import { SxProps, Stack, Typography, Checkbox, FormControlLabel, ToggleButton, Button, ToggleButtonGroup, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import { useEffect, useRef, useState } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { CalendarIcon, DateCalendar, TimePicker } from '@mui/x-date-pickers';
import moment, { Moment } from 'moment-timezone';
import { DAYS, getPredefinedRange, RANGES } from './predefineRanges';
import merge from 'lodash.merge';

interface DateTimeRangeSelectorProps {
    timezone?: string;
    disabled?: boolean;
    disablePast?: boolean;
    disableFuture?: boolean;
    disableDefinedRange?: boolean;
    disableTimeAndDays?: boolean;
    defaultOpen?: boolean;
    alwaysOpen?: boolean;
    displayVertical?: boolean | undefined;
    defaultTime?: { start: Moment | null; end: Moment | null };
    sx?: {
        container?: SxProps<Theme>;
        button?: SxProps<Theme>;
        menu?: SxProps<Theme>;
        calendar?: SxProps<Theme>;
        leftSection?: SxProps<Theme>;
        rightSection?: SxProps<Theme>;
    };
    onChange?: (dateTimeRange: DateTimeRange) => void;
}

export type Range = { start: Moment | null; end: Moment | null };

export type DateTimeRange = {
    timezone: string;
    dateRange: {
        start: Moment | null;
        end: Moment | null;
    };
    timeRange?: {
        start: Moment | null;
        end: Moment | null;
    };
    days?: number[];
    definedRange: string;
};

interface DateState extends Omit<DateTimeRange, 'days'> {
    days: boolean[];
    isAllDay: boolean;
}

const useStyles = (theme: Theme, isVertical: boolean | undefined): { [key: string]: SxProps } => ({
    openButton: {
        border: '1px solid',
        borderRadius: 1,
        backgroundColor: theme.palette.background.default,
        borderColor: theme.palette.border.main,
        color: theme.palette.text.primary,
        textTransform: 'none',
        ':hover': {
            backgroundColor: theme.palette.background.paper
        }
    },
    relativePosition: {
        position: 'relative'
    },
    menuContainer: {
        backgroundColor: theme.palette.background.default,
        position: 'absolute',
        top: '4px',
        zIndex: '10',
        flexDirection: isVertical ? 'column' : 'row',
        maxHeight: isVertical ? 'fit-content' : 334,
        overflow: 'hidden',
        border: '1px solid',
        borderRadius: 1,
        borderColor: theme.palette.border.main
    },
    leftSection: {
        padding: 2,
        minHeight: 80,
        width: isVertical ? 300 : 150,
        overflowY: isVertical ? 'hidden' : 'scroll',
        overflowX: isVertical ? 'scroll' : 'hidden'
    },
    clearButton: {
        padding: '0',
        textTransform: 'none',
        justifyContent: 'end',
        borderRadius: 1,
        color: theme.palette.text.primary
    },
    daysTimeHeader: {
        flexDirection: 'row',
        margin: 0,
        justifyContent: 'space-between',
        alignItems: 'center',
        fontSize: 14,
        opacity: '50%'
    },
    allDayLabel: { width: 'fit-content' },
    rangesButtonGroup: {
        gap: isVertical ? 2 : 0,
        border: 0,
        borderRadius: 0,
        '.MuiToggleButtonGroup-grouped:not(:last-of-type)': {
            borderRadius: 1
        },
        '.MuiToggleButtonGroup-grouped:not(:first-of-type)': {
            borderRadius: 1
        }
    },
    toggleDefinedRange: {
        border: 0,
        width: '100%',
        padding: '6px',
        textAlign: 'left',
        height: 'fit-content',
        justifyContent: 'start',
        textTransform: 'none',
        textWrap: isVertical ? 'nowrap' : 'wrap',
        borderRadius: 1,
        color: theme.palette.text.primary,
        '&.Mui-selected, &.Mui-selected:hover': {
            color: theme.palette.text.dark,
            backgroundColor: theme.palette.action.selected
        }
    },
    toggleDayButton: {
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        padding: 0,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.text.primary,
        '&.Mui-selected, &.Mui-selected:hover': {
            color: theme.palette.text.dark,
            backgroundColor: theme.palette.action.selected
        }
    },
    timePickerContainer: {
        width: '70%',
        marginBottom: 2,
        gap: 1
    },
    weekDaysContainer: {
        flexDirection: 'row',
        gap: 0.5,
        marginTop: 0.5
    },
    calendar: {
        borderRight: isVertical ? 0 : 1,
        borderLeft: isVertical ? 0 : 1,
        borderTop: isVertical ? 1 : 0,
        borderBottom: isVertical ? 1 : 0,
        borderColor: isVertical ? theme.palette.border.main : theme.palette.border.main,
        button: {
            borderRadius: 1
        },
        svg: {
            color: theme.palette.text.primary
        }
    }
});

export const DateTimeRangeSelector = (props: DateTimeRangeSelectorProps) => {
    const {
        timezone = 'system',
        defaultOpen = false,
        alwaysOpen = false,
        displayVertical = undefined,
        disabled,
        disablePast,
        disableFuture,
        disableDefinedRange,
        disableTimeAndDays,
        defaultTime,
        onChange,
        sx
    } = props;
    const theme = useTheme();

    // Display vertical overrides responsiveness if set
    const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
    const isVertical = displayVertical === undefined ? isMediumScreen : displayVertical;

    const styles = useStyles(theme, isVertical);

    const defaultStartTime = defaultTime?.start ?? moment('6:00:00', 'hh:mm:ss');
    const defaultEndTime = defaultTime?.end ?? moment('18:00:00', 'hh:mm:ss');

    const [dateState, setDateState] = useState<DateState>({
        timezone,
        dateRange: { start: null, end: null },
        timeRange: { start: defaultStartTime, end: defaultEndTime },
        days: [true, true, true, true, true, true, true],
        isAllDay: true,
        definedRange: 'Custom'
    });

    const [isOpen, setIsOpen] = useState(defaultOpen || alwaysOpen);
    const [isStartTimeOpen, setIsStartTimeOpen] = useState(false);
    const [isEndTimeOpen, setIsEndTimeOpen] = useState(false);

    const menuRef = useRef<HTMLDivElement | null>(null);
    const startTimeInputRef = useRef<HTMLDivElement | null>(null);
    const endTimeInputRef = useRef<HTMLDivElement | null>(null);

    const dateStateRef = useRef(dateState);

    const isRangeOnSameYear = dateState.dateRange.start?.format('YYYY') === dateState.dateRange.end?.format('YYYY');
    const showFormat = dateState.dateRange.end && !isRangeOnSameYear ? 'Do MMMM YYYY' : 'Do MMMM';

    useEffect(() => {
        dateStateRef.current = dateState;
    }, [dateState]);

    useEffect(() => {
        const handleClickOutside = (event: Event) => {
            if (
                !alwaysOpen &&
                menuRef.current &&
                !menuRef.current.contains(event.target as Node) &&
                !startTimeInputRef.current?.contains(event.target as Node) &&
                !endTimeInputRef.current?.contains(event.target as Node)
            ) {
                const currentState = dateStateRef.current;

                if (currentState.dateRange.start && !currentState.dateRange.end) {
                    setDateState({
                        ...currentState,
                        dateRange: { start: currentState.dateRange.start, end: currentState.dateRange.start }
                    });
                    triggerChange({
                        definedRange: currentState.definedRange,
                        dateRange: { start: currentState.dateRange.start, end: currentState.dateRange.start },
                        timeRange: currentState.timeRange,
                        days: currentState.days,
                        isAllDay: currentState.isAllDay
                    });
                }

                setIsOpen(false);
            }
        };

        const handleClickOutsideTimePoppers = (event: Event) => {
            if (!startTimeInputRef.current?.contains(event.target as Node)) {
                setIsStartTimeOpen(false);
            }

            if (!endTimeInputRef.current?.contains(event.target as Node)) {
                setIsEndTimeOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('mousedown', handleClickOutsideTimePoppers);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleDateChange = (newDateRange: { start: Moment | null; end: Moment | null }) => {
        setDateState({ ...dateState, definedRange: 'Custom', dateRange: newDateRange });
        triggerChange({ dateRange: newDateRange, definedRange: 'Custom' });
    };

    const handleTimeChange = (type: 'start' | 'end', time: Moment | null) => {
        const newTimeRange: Range = {
            start: type === 'start' ? time : (dateState.timeRange?.start ?? null),
            end: type === 'end' ? time : (dateState.timeRange?.end ?? time)
        };

        setDateState({ ...dateState, timeRange: newTimeRange });
        triggerChange({ timeRange: newTimeRange });
    };

    const handleDaysChange = (toggleIndex: number) => {
        setDateState(prevState => {
            const updatedWeekDays = [...prevState.days];
            updatedWeekDays[toggleIndex] = !updatedWeekDays[toggleIndex];
            triggerChange({ days: updatedWeekDays });
            return { ...prevState, days: updatedWeekDays };
        });
    };

    const handleDefinedRangeChange = (predefineRange: string) => {
        const { start, end } = getPredefinedRange(predefineRange, disablePast, disableFuture);
        setDateState({ ...dateState, definedRange: predefineRange, dateRange: { start, end } });
        triggerChange({ dateRange: { start, end }, definedRange: predefineRange });
    };

    const handleSelectAllDay = (toggleAllDay: boolean) => {
        setDateState({ ...dateState, isAllDay: toggleAllDay });
        triggerChange({ isAllDay: toggleAllDay });
    };

    const handleClear = () => {
        const timeReset = { start: defaultStartTime, end: defaultEndTime };
        const daysReset = Array(dateState.days.length).fill(true);

        setDateState({ ...dateState, timeRange: timeReset, days: daysReset });
        triggerChange({ timeRange: timeReset, days: daysReset });
    };

    const triggerChange = (updates: { definedRange?: string; dateRange?: Range; timeRange?: Range; days?: boolean[]; isAllDay?: boolean }) => {
        if (!onChange) return;

        const finalDefinedRange = updates.definedRange ?? dateState.definedRange;
        const finalDateRange = updates.dateRange ?? dateState.dateRange;
        const finalTimeRange = updates.timeRange ?? dateState.timeRange;
        const finalDays = updates.days ?? dateState.days;
        const finalAllDay = updates.isAllDay !== undefined ? updates.isAllDay : dateState.isAllDay;

        const payload = {
            timezone,
            dateRange: finalDateRange,
            timeRange: finalAllDay ? undefined : finalTimeRange,
            days: DAYS.filter(day => finalDays[day.value]).map(day => day.value),
            definedRange: finalDefinedRange
        };

        onChange(payload);
    };

    return (
        <Stack ref={menuRef} sx={sx?.container}>
            <Button
                variant="contained"
                startIcon={<CalendarIcon />}
                onClick={() => setIsOpen(!isOpen)}
                sx={merge(styles.openButton, sx?.button)}
                disabled={disabled}>
                {dateState.dateRange.start ? dateState.dateRange.start?.format(showFormat) : 'Select date range'}{' '}
                {dateState.dateRange.end ? '- ' + dateState.dateRange.end?.format(showFormat) : ''}
            </Button>
            {isOpen && (
                <Stack sx={styles.relativePosition}>
                    <LocalizationProvider dateLibInstance={moment} dateAdapter={AdapterMoment}>
                        <Stack sx={merge(styles.menuContainer, sx?.menu)}>
                            {!disableDefinedRange && (
                                <Stack sx={merge(styles.leftSection, sx?.leftSection)}>
                                    <ToggleButtonGroup
                                        orientation={isVertical ? 'horizontal' : 'vertical'}
                                        exclusive
                                        value={dateState.definedRange}
                                        sx={styles.rangesButtonGroup}
                                        onChange={(_, value) => handleDefinedRangeChange(value as string)}>
                                        {RANGES.map((predefineRange, index) => (
                                            <ToggleButton key={index} value={predefineRange} sx={styles.toggleDefinedRange}>
                                                {predefineRange}
                                            </ToggleButton>
                                        ))}
                                    </ToggleButtonGroup>
                                </Stack>
                            )}

                            <RangeDateCalendar
                                timezone={timezone}
                                start={dateState.dateRange.start}
                                end={dateState.dateRange.end}
                                disablePast={disablePast}
                                disableFuture={disableFuture}
                                displayVertical={isVertical}
                                onChange={handleDateChange}
                                sx={merge(styles.calendar, sx?.calendar)}
                            />

                            {!disableTimeAndDays && (
                                <Stack padding={2} sx={merge(styles.rightSection, sx?.rightSection)}>
                                    <Stack sx={styles.daysTimeHeader}>
                                        <Typography>Time & Days</Typography>
                                        <Button variant="text" sx={styles.clearButton} onClick={handleClear}>
                                            Clear
                                        </Button>
                                    </Stack>

                                    <FormControlLabel
                                        control={<Checkbox checked={dateState.isAllDay} onChange={() => handleSelectAllDay(!dateState.isAllDay)} />}
                                        label="All day"
                                        sx={styles.allDayLabel}
                                    />

                                    {!dateState.isAllDay && (
                                        <Stack sx={styles.timePickerContainer}>
                                            <div
                                                onClick={() => {
                                                    if (!isStartTimeOpen) {
                                                        setIsStartTimeOpen(true);
                                                    }
                                                }}>
                                                <TimePicker
                                                    open={isStartTimeOpen}
                                                    slotProps={{
                                                        popper: {
                                                            ref: startTimeInputRef,
                                                            onClick: e => e.stopPropagation()
                                                        }
                                                    }}
                                                    timezone={timezone}
                                                    label="Start"
                                                    onChange={moment => handleTimeChange('start', moment)}
                                                    value={dateState.timeRange?.start}
                                                />
                                            </div>
                                            <div
                                                onClick={() => {
                                                    if (!isEndTimeOpen) {
                                                        setIsEndTimeOpen(true);
                                                    }
                                                }}>
                                                <TimePicker
                                                    open={isEndTimeOpen}
                                                    slotProps={{
                                                        popper: {
                                                            ref: endTimeInputRef,
                                                            onClick: e => e.stopPropagation()
                                                        }
                                                    }}
                                                    timezone={timezone}
                                                    label="End"
                                                    disabled={!dateState.timeRange?.start}
                                                    onChange={moment => handleTimeChange('end', moment)}
                                                    value={dateState.timeRange?.end}
                                                    minTime={dateState.timeRange?.start ?? undefined}
                                                    onError={() => {
                                                        setDateState({
                                                            ...dateState,
                                                            timeRange: {
                                                                start: dateState.timeRange?.start ?? null,
                                                                end: dateState.timeRange?.start ?? null
                                                            }
                                                        });
                                                    }}
                                                />
                                            </div>
                                        </Stack>
                                    )}

                                    <Typography>Days</Typography>
                                    <Stack sx={styles.weekDaysContainer}>
                                        {DAYS.map(day => (
                                            <ToggleButton
                                                key={day.value}
                                                value={day.value}
                                                selected={dateState.days[day.value]}
                                                sx={styles.toggleDayButton}
                                                onChange={() => handleDaysChange(day.value)}>
                                                {day.label}
                                            </ToggleButton>
                                        ))}
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </LocalizationProvider>
                </Stack>
            )}
        </Stack>
    );
};

type RangeDateCalendarProps = {
    start: Moment | null;
    end: Moment | null;
    timezone: string;
    disablePast?: boolean;
    disableFuture?: boolean;
    displayVertical?: boolean;
    sx?: SxProps<Theme>;
    onChange: (newRange: { start: Moment | null; end: Moment | null }) => void;
};

function RangeDateCalendar({
    start,
    end,
    timezone,
    disablePast = false,
    disableFuture = false,
    displayVertical = false,
    sx,
    onChange
}: RangeDateCalendarProps) {
    const theme = useTheme();
    const styles = useStyles(theme, displayVertical);

    const handleDateChange = (newDate: Moment | null) => {
        if (!onChange) return;

        if (start && !end) {
            const isBeforeStart = newDate && start.isAfter(newDate);
            onChange({
                start: isBeforeStart ? newDate : start,
                end: isBeforeStart ? start : newDate
            });
        } else {
            onChange({ start: newDate, end: null });
        }
    };

    const isInRange = (date: Moment) => {
        if (!start || !end) return false;
        return date.isSameOrAfter(start) && date.isSameOrBefore(end);
    };

    const rangeHighlightStyle = (date: Moment) => {
        if (!start || !end) return {};

        const isStart = date.isSame(start, 'day');
        const isEnd = date.isSame(end, 'day');

        if (isStart && isEnd) {
            return {
                backgroundColor: theme.palette.action.selected,
                color: 'white'
            };
        }

        if (isStart || isEnd) {
            return {
                backgroundColor: theme.palette.action.selected,
                color: 'white',
                borderTopRightRadius: isStart ? 0 : undefined,
                borderBottomRightRadius: isStart ? 0 : undefined,
                borderTopLeftRadius: isEnd ? 0 : undefined,
                borderBottomLeftRadius: isEnd ? 0 : undefined
            };
        }

        if (isInRange(date)) {
            return {
                backgroundColor: 'gray',
                borderRadius: 0
            };
        }

        return {};
    };

    return (
        <DateCalendar
            timezone={timezone}
            disablePast={disablePast}
            disableFuture={disableFuture}
            value={start}
            sx={merge(styles.calendar, sx)}
            slotProps={{
                day: ownerState => ({
                    style: rangeHighlightStyle(ownerState.day),
                    onClick: () => handleDateChange(ownerState.day)
                })
            }}
        />
    );
}
